import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Button, FormControlLabel, Grid, InputAdornment, InputLabel, Switch, } from '@mui/material';
import { CirclePicker } from 'react-color';
import { setIn } from 'immutable';
import { UniversalDialog } from 'console/dialogs/universal';
import { StrictNumberInput } from 'shared/components/fields';
import { all, validate_by_specification } from 'z3k-parser/validation/collections';
import { minMax, minMaxStr, notEmpty } from 'z3k-parser/validation/basic';
import { unpack_validation } from 'z3k-parser/validation/types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { GeozoneTypes, } from '../../model/types';
import { findGeozoneFromId, getPropertiesFromGeozone } from '../../utils/utils';
import { createGeozone, deleteGeozoneFromList, editGeozoneProperties, } from '../../model/actions';
import { Control } from 'shared/components/fields/control';
import { TextInputWrapper } from 'shared/components/fields/text';
export const initial_geozone_properties = {
    name: 'Новая геозона',
    description: '',
    color: '#3388ff',
    notifiable: false,
    overspeed: false,
    speed: 40,
    overspeed_notification: false,
    id: '',
    type: GeozoneTypes.Polygon,
};
const default_color = [
    '#ff0000',
    '#e91e63',
    '#9900ef',
    '#673ab7',
    '#0000ff',
    '#2196f3',
    '#3388ff',
    '#00bcd4',
    '#009688',
    '#4caf50',
    '#33ff3b',
    '#cddc39',
    '#ffeb3b',
    '#ffc107',
    '#ff8000',
    '#ff5722',
    '#795548',
    '#607d8b',
];
const customColor = (color) => {
    if (default_color.indexOf(color) === -1)
        return setIn(default_color, [default_color.length - 1], color);
    return default_color;
};
export function data2state(input_data) {
    return {
        name: input_data.name ?? initial_geozone_properties.name,
        description: input_data.description ?? initial_geozone_properties.description,
        color: input_data.color ?? initial_geozone_properties.color,
        notifiable: input_data.notifiable ?? initial_geozone_properties.notifiable,
        overspeed: input_data.overspeed ?? initial_geozone_properties.overspeed,
        speed: input_data.speed ?? initial_geozone_properties.speed,
        overspeed_notification: input_data.overspeed_notification ??
            initial_geozone_properties.overspeed_notification,
    };
}
export function state2data(dialog_local_state) {
    return {
        ...dialog_local_state,
        name: dialog_local_state.name.trim(),
        //для совместимости со старым ПО
        //@ts-ignore
        OverSpeed: dialog_local_state.overspeed,
    };
}
function GeozonePropertiesDialogImpl(props) {
    const { open, onCloseDialog, geozone_id, new_geozone, geozones, onAddGeozone, onEditGeozones, onDeleteGeozone, } = props;
    const geozone = geozone_id == null ? undefined : findGeozoneFromId(geozones, geozone_id);
    const [dialog_local_state, setDialogLocalState] = useState(data2state(geozone == null
        ? initial_geozone_properties
        : getPropertiesFromGeozone(geozone)));
    useEffect(() => {
        if (open) {
            setDialogLocalState(data2state(geozone == null
                ? initial_geozone_properties
                : getPropertiesFromGeozone(geozone)));
        }
    }, [setDialogLocalState, geozone, open]);
    const onChangeLocalState = useCallback((key, value) => {
        setDialogLocalState({
            ...dialog_local_state,
            [key]: value,
        });
    }, [setDialogLocalState, dialog_local_state]);
    const onChangeGeozoneName = useCallback((new_value) => onChangeLocalState('name', new_value), [onChangeLocalState]);
    const onChangeGeozoneDescription = useCallback((new_value) => onChangeLocalState('description', new_value), [onChangeLocalState]);
    const onChangeGeozoneColor = useCallback((color) => onChangeLocalState('color', color.hex), [onChangeLocalState]);
    const onChangeGeozoneNotification = useCallback(() => onChangeLocalState('notifiable', !dialog_local_state.notifiable), [onChangeLocalState]);
    const onChangeGeozoneOverSpeed = useCallback(() => onChangeLocalState('overspeed', !dialog_local_state.overspeed), [onChangeLocalState]);
    const onChangeGeozoneOverSpeedValue = useCallback((new_value) => onChangeLocalState('speed', new_value), [onChangeLocalState]);
    const onChangeGeozoneOverSpeedNotification = useCallback(() => onChangeLocalState('overspeed_notification', !dialog_local_state.overspeed_notification), [onChangeLocalState]);
    const validate = React.useMemo(() => validate_by_specification({
        name: all(notEmpty, minMaxStr(0, 150)),
        description: all(minMaxStr(0, 300)),
        speed: all(minMax(5, 150)),
    })(dialog_local_state), [dialog_local_state]);
    const onAcceptClick = useCallback(() => {
        //Это новая геозона, её надо создать
        if (geozone_id == null && new_geozone) {
            const { color, ...rest } = state2data(dialog_local_state);
            const create_geozone = {
                ...new_geozone,
                properties: {
                    ...new_geozone.properties,
                    ...rest,
                    style: {
                        ...new_geozone.style,
                        color,
                    },
                },
            };
            onAddGeozone(create_geozone);
        }
        if (geozone_id != null && geozone) {
            const { color, ...rest } = state2data(dialog_local_state);
            onEditGeozones({
                ...geozone,
                geojson: {
                    ...geozone.geojson,
                    properties: {
                        ...geozone.geojson.properties,
                        ...rest,
                        style: {
                            ...geozone.geojson.style,
                            color,
                        },
                    },
                },
            });
        }
        onCloseDialog();
    }, [
        onCloseDialog,
        geozone_id,
        new_geozone,
        geozone,
        dialog_local_state,
        onAddGeozone,
        onEditGeozones,
    ]);
    const deleteGeozone = useCallback(() => {
        if (geozone != null) {
            onDeleteGeozone(geozone);
            onCloseDialog();
        }
    }, [onDeleteGeozone, onCloseDialog, geozone]);
    return (_jsx(UniversalDialog, { open: open, onClose: onCloseDialog, "aria-labelledby": "geozone-properties-dialog", title: "\u041F\u0430\u0440\u0430\u043C\u0435\u0442\u0440\u044B \u0433\u0435\u043E\u0437\u043E\u043D\u044B", footer: _jsxs(Grid, { container: true, spacing: 2, alignItems: "center", justifyContent: "space-between", children: [_jsx(Grid, { item: true, children: _jsx(Button, { onClick: deleteGeozone, color: "secondary", disabled: geozone_id == null, children: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C" }) }), _jsxs(Grid, { item: true, children: [_jsx(Button, { onClick: onCloseDialog, color: "primary", children: "\u041E\u0442\u043C\u0435\u043D\u0430" }), _jsx(Button, { onClick: onAcceptClick, disabled: validate != null, color: "primary", children: "\u041F\u0440\u0438\u043D\u044F\u0442\u044C" })] })] }), children: _jsxs(Grid, { container: true, spacing: 2, alignItems: "center", justifyContent: "flex-start", children: [_jsx(Grid, { item: true, xs: 12, children: _jsx(Control, { label: "\u0418\u043C\u044F \u0433\u0435\u043E\u0437\u043E\u043D\u044B", value: dialog_local_state.name, onChange: onChangeGeozoneName, field: (input_props) => (_jsx(TextInputWrapper, { ...input_props, autoFocus: true })), help_id: "geozone-name", validation_result: unpack_validation(validate, 'name'), validation_warnings_result: null }) }), _jsx(Grid, { item: true, xs: 12, children: _jsx(Control, { label: "\u041E\u043F\u0438\u0441\u0430\u043D\u0438\u0435 \u0433\u0435\u043E\u0437\u043E\u043D\u044B", value: dialog_local_state.description, onChange: onChangeGeozoneDescription, field: (input_props) => (_jsx(TextInputWrapper, { ...input_props, autoFocus: true })), help_id: "geozone-description", validation_result: unpack_validation(validate, 'description'), validation_warnings_result: null }) }), _jsx(Grid, { item: true, xs: 12, children: _jsx(InputLabel, { htmlFor: "color-adornment-amount", children: "\u0426\u0432\u0435\u0442 \u0433\u0435\u043E\u0437\u043E\u043D\u044B" }) }), _jsx(Grid, { item: true, xs: 12, children: _jsx(CirclePicker, { color: dialog_local_state.color, colors: customColor(dialog_local_state.color), circleSize: 28, circleSpacing: 14, onChangeComplete: onChangeGeozoneColor }) }), _jsx(Grid, { item: true, xs: 12, children: _jsx(FormControlLabel, { control: _jsx(Switch, { checked: dialog_local_state.notifiable, onChange: onChangeGeozoneNotification, name: "notification", color: "primary" }), label: "\u041F\u043E\u043B\u0443\u0447\u0430\u0442\u044C \u0443\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u044F \u043E\u00A0\u043F\u043E\u0441\u0435\u0449\u0435\u043D\u0438\u0438" }) }), (geozone != null
                    ? geozone.geojson.geometry.type
                    : new_geozone?.geometry.type) === GeozoneTypes.Polygon ? (_jsxs(_Fragment, { children: [_jsxs(Grid, { item: true, xs: 12, sm: 6, children: [_jsx(FormControlLabel, { control: _jsx(Switch, { checked: dialog_local_state.overspeed, onChange: onChangeGeozoneOverSpeed, name: "overspeed", color: "secondary" }), label: "\u041E\u0433\u0440\u0430\u043D\u0438\u0447\u0435\u043D\u0438\u0435 \u0441\u043A\u043E\u0440\u043E\u0441\u0442\u0438" }), _jsx(FormControlLabel, { control: _jsx(Switch, { checked: dialog_local_state.overspeed_notification, onChange: onChangeGeozoneOverSpeedNotification, name: "overspeed_notification", color: "secondary", disabled: !dialog_local_state.overspeed }), label: "\u041F\u043E\u043B\u0443\u0447\u0430\u0442\u044C \u0443\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u044F \u043E\u00A0\u043F\u0440\u0435\u0432\u044B\u0448\u0435\u043D\u0438\u0438 \u0441\u043A\u043E\u0440\u043E\u0441\u0442\u0438" })] }), _jsx(Grid, { item: true, xs: 12, sm: 6, children: _jsx(Control, { label: "\u041C\u0430\u043A\u0441\u0438\u043C\u0430\u043B\u044C\u043D\u0430\u044F \u0441\u043A\u043E\u0440\u043E\u0441\u0442\u044C", value: dialog_local_state.speed, onChange: onChangeGeozoneOverSpeedValue, field: (input_props) => (_jsx(StrictNumberInput, { ...input_props, autoFocus: true, disabled: !dialog_local_state.overspeed, endAdornment: _jsx(InputAdornment, { position: "end", children: "\u043A\u043C/\u0447" }) })), help_id: "geozone-over-speed", validation_result: unpack_validation(validate, 'speed'), validation_warnings_result: null }) })] })) : null] }) }));
}
export const GeozonePropertiesDialog = connect(createStructuredSelector({
    geozones: (state) => state.geozones,
}), {
    onAddGeozone: createGeozone,
    onEditGeozones: editGeozoneProperties,
    onDeleteGeozone: deleteGeozoneFromList,
})(GeozonePropertiesDialogImpl);
